import { useEffect, useState } from "react";
import { Sparkles, ImageOff } from "lucide-react";
import { infographicSectionImageUrl } from "@/lib/api";

/* Section-scoped AI infographic.
 * Renders one Gemini Nano Banana image for a specific (slug, section) pair.
 *
 * Props:
 *   slug     — feature slug from FEATURE_CATALOG (backend)
 *   section  — one of: overview | how-it-works | outcomes | guardrails
 *   theme    — "dark" (default, for dark section bg) | "light" (for cream bg)
 *   accent   — hex accent color to tint the shadow / border glow
 *   caption  — small caption under the image (optional)
 */
export default function SectionInfographic({
  slug, section, theme = "dark", accent = "#4F46E5", caption,
}) {
  const [errored, setErrored] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setErrored(false); setLoaded(false); }, [slug, section]);

  const isDark = theme === "dark";
  const wrapperCls = isDark
    ? "border border-white/10 bg-slate-950"
    : "border border-[#ebe4d3] bg-white";
  const captionCls = isDark ? "text-white/60" : "text-slate-500";

  if (!slug || !section) return null;

  return (
    <figure
      className={`relative rounded-3xl overflow-hidden ${wrapperCls} shadow-2xl`}
      style={{ boxShadow: `0 25px 55px -20px ${accent}55` }}
      data-testid={`section-infographic-${slug}-${section}`}
    >
      <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/40 backdrop-blur px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white/85">
        <Sparkles className="w-3 h-3" /> AI infographic
      </div>
      {errored ? (
        <div className="aspect-[16/9] flex items-center justify-center gap-2 p-8 text-center text-slate-500 bg-slate-100">
          <ImageOff className="w-5 h-5" /> Rendering… refresh in a moment.
        </div>
      ) : (
        <img
          src={infographicSectionImageUrl(slug, section)}
          alt={`${slug} — ${section} infographic`}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => setErrored(true)}
          className={`w-full h-auto block transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"}`}
          data-testid={`section-infographic-${slug}-${section}-img`}
        />
      )}
      {caption && (
        <figcaption className={`px-4 py-2 text-[11px] text-center ${captionCls}`}>
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
